import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCartContext } from '../../context/CartContext';
import { formatDateTime } from '../../utils/DateUtils';
import DateTimePicker from './DateTimePicker';
import InfoContent from './InfoContent';

const UpdateCardModal = ({ card, index, onClose, store, mode = 'update' }) => {
    const { t } = useTranslation();
    const { updateCard, copyCardToCart } = useCartContext();
    const [infoField, setInfoField] = useState('');
    const [dateTimeMode, setDateTimeMode] = useState(card.dateTimeMode || 'now');
    const modalRef = useRef(null);
    const previousFocusRef = useRef(null);
    
    useEffect(() => {
        previousFocusRef.current = document.activeElement;
        
        return () => {
            if (previousFocusRef.current) {
                previousFocusRef.current.focus();
            }
        };
    }, []);
    
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        
        if (modalRef.current) {
            const focusableElements = modalRef.current.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            
            if (focusableElements.length > 0) {
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];
                
                firstElement.focus();
                
                const trapFocus = (e) => {
                    if (e.key === 'Tab' && !e.shiftKey && document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement.focus();
                    }
                    
                    if (e.key === 'Tab' && e.shiftKey && document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement.focus();
                    }
                };
                
                document.addEventListener('keydown', trapFocus);
                
                return () => {
                    document.removeEventListener('keydown', handleKeyDown);
                    document.removeEventListener('keydown', trapFocus);
                };
            }
        }
        
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);
    
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);
    const [formData, setFormData] = useState({
        desfirstname: mode === 'update' ? card.firstNameReceiver : '',
        deslastname: mode === 'update' ? card.lastNameReceiver : '',
        desemail: mode === 'update' ? card.emailReceiver : '',
        desconfEmail: mode === 'update' ? card.emailReceiver : '',
        date: mode === 'update' ? card.sendDate?.split(' ')[0] : '',
        time: mode === 'update' ? card.sendDate?.split(' ')[1] : '',
        cardLanguage: mode === 'update' ? card.cardLanguage : store.default_language,
    });

    const [errors, setErrors] = useState({
        desfirstname: '',
        deslastname: '',
        desemail: '',
        desconfEmail: '',
        dateTime: ''
    });

    const handleShowInfo = (field) => {
        setInfoField(field);
    };

    const handleHideInfo = () => {
        setInfoField('');
    };

    const handleDateTimeMode = (mode) => {
        setDateTimeMode(mode);

        if (mode === 'now') {
            const currentDateTime = formatDateTime.syncDateTime('now', store);
            setFormData(prev => ({
                ...prev,
                date: currentDateTime.date,
                time: currentDateTime.time,
                datepicker: currentDateTime.datepicker,
                timepicker: currentDateTime.timepicker,
                sendDate: `${currentDateTime.date} ${currentDateTime.time}`,
                sendDateTmp: Math.floor(new Date(`${currentDateTime.date} ${currentDateTime.time}`).getTime() / 1000)
            }));
        } else if (mode === 'after' && !formData.datepicker) {
            const nextDateTime = formatDateTime.syncDateTime('after', store);
            setFormData(prev => ({
                ...prev,
                date: nextDateTime.date,
                time: nextDateTime.time,
                datepicker: nextDateTime.datepicker,
                timepicker: nextDateTime.timepicker,
                sendDate: `${nextDateTime.date} ${nextDateTime.time}`,
                sendDateTmp: Math.floor(new Date(`${nextDateTime.date} ${nextDateTime.time}`).getTime() / 1000)
            }));
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        if (!formData.desfirstname || formData.desfirstname.length > 25) {
            newErrors.desfirstname = t("Votre prénom ne doit pas dépasser 25 caractères");
            isValid = false;
        }

        if (!formData.deslastname || formData.deslastname.length > 25) {
            newErrors.deslastname = t("Votre prénom ne doit pas dépasser 25 caractères");
            isValid = false;
        }

        if (!formData.isSelfDestination) {
            if (!formData.desemail || !/\S+@\S+\.\S+/.test(formData.desemail)) {
                newErrors.desemail = t("Veuillez saisir un email valide");
                isValid = false;
            }

            if (formData.desemail !== formData.desconfEmail) {
                newErrors.desconfEmail = t("Les emails renseignés sont différents");
                isValid = false;
            }

            if (dateTimeMode === 'after' && (!formData.date || !formData.time)) {
                newErrors.dateTime = t("La date et l'heure sont requises");
                isValid = false;
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        const sendDateStr = `${formData.date} ${formData.time}`;
        const updatedCard = {
            firstNameReceiver: formData.desfirstname,
            lastNameReceiver: formData.deslastname,
            emailReceiver: formData.desemail,
            isSelfDestination: formData.isSelfDestination,
            cardLanguage: formData.cardLanguage,
            dateTimeMode,
            sendDate: sendDateStr,
            sendDateTmp: Math.floor(new Date(sendDateStr).getTime() / 1000),
            momentSend: dateTimeMode
        };
        if (mode === 'update') {
            updateCard(index, updatedCard);
        } else {
            copyCardToCart(index, updatedCard);
        }
        onClose();
    };
    
    const FieldError = ({ error, id }) => {
        if (!error) return null;
    
        return (
          <div className="field-error" id={`error-${id}`} role="alert">
            <span className="error-icon" aria-hidden="true">⚠️</span>
            <span className="error-message">{error}</span>
          </div>
        );
      };
    
    const AccessibleLink = ({ onClick, className, children, ariaLabel }) => (
        <a 
            href="#"
            onClick={(e) => {
                e.preventDefault();  
                onClick(e);
              }}
            className={className}
            aria-label={ariaLabel || undefined}
            role="button"
            tabIndex="0"
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick(e);
                }
            }}
        >
            {children}
        </a>
    );
    
    return (
        <div
            id="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modalTitle"
            style={{ display: 'block' }}
            className={!store.exception ? '' : 'hidden'}
            aria-hidden={store.exception ? "true" : "false"}
        >
            <div id="modalBox" className="centerPopup" ref={modalRef} tabIndex="-1">
                <AccessibleLink
                    onClick={onClose}
                    className="closeInfoContent info"
                    ariaLabel={t("Fermer")}
                ></AccessibleLink>
                <div id="cardInfo" className="modalcontent">
                    <h2 id="modalTitle" className="sr-only">
                        {mode === 'update' ? t("Modifier la carte") : t("Copier la carte")}
                    </h2>
                    <form method="post" onSubmit={handleSubmit} noValidate>
                        <div className="fieldsGroup">
                            {!store.isProSignboard && (
                                <>
                                    <div className="formField" id="row_desfirstname">
                                        <div className="formControl">
                                            <input
                                                type="text"
                                                name="desfirstname"
                                                id="desfirstname"
                                                value={formData.desfirstname}
                                                onChange={handleChange}
                                                required
                                                placeholder={t("Prénom") + "*"}
                                                aria-label={t("Prénom")}
                                                aria-required="true"
                                                aria-invalid={errors.desfirstname ? "true" : "false"}
                                                aria-describedby={errors.desfirstname ? "error-desfirstname" : undefined}
                                            />
                                        </div>
                                        <FieldError id="desfirstname" error={errors.desfirstname} />
                                    </div>

                                    <div className="formField" id="row_deslastname">
                                        <div className="formControl">
                                            <input
                                                type="text"
                                                name="deslastname"
                                                id="deslastname"
                                                value={formData.deslastname}
                                                onChange={handleChange}
                                                required
                                                placeholder={t("Nom") + "*"}
                                                aria-label={t("Nom")}
                                                aria-required="true"
                                                aria-invalid={errors.deslastname ? "true" : "false"}
                                                aria-describedby={errors.deslastname ? "error-deslastname" : undefined}
                                            />
                                        </div>
                                        <FieldError id="deslastname" error={errors.deslastname} />
                                    </div>

                                    {!formData.isSelfDestination && (
                                        <>
                                            <div className="formField" id="row_desemail">
                                                <div className="formControl hasInfo">
                                                    <input
                                                        type="email"
                                                        name="desemail"
                                                        id="desemail"
                                                        value={formData.desemail}
                                                        onChange={handleChange}
                                                        required
                                                        placeholder={t("Email") + "*"}
                                                        aria-label={t("Email")}
                                                        aria-required="true"
                                                        aria-invalid={errors.desemail ? "true" : "false"}
                                                        aria-describedby={errors.desemail ? "error-desemail" : undefined}
                                                    />
                                                    <InfoContent
                                                        id="forEmailReceiver"
                                                        infoField={infoField}
                                                        showInfo={handleShowInfo}
                                                        hideInfo={handleHideInfo}
                                                        context="emailModalUpdate"
                                                        infoText={
                                                            <>
                                                                <h5 className="typoMedium">{t("La carte cadeau sera envoyée par email")}</h5>
                                                                <p>{t("Veuillez privilégier une adresse personnelle car les serveurs professionnels peuvent filtrer les informations commerciales")}</p>
                                                            </>
                                                        }
                                                        ariaLabelShow={t("Informations sur l'email")}
                                                        ariaLabelHide={t("Fermer les informations")}
                                                    />
                                                </div>
                                                <FieldError id="desemail" error={errors.desemail} />
                                            </div>

                                            <div className="formField" id="row_desconfEmail">
                                                <div className="formControl">
                                                    <input
                                                        type="email"
                                                        name="desconfEmail"
                                                        id="desconfEmail"
                                                        value={formData.desconfEmail}
                                                        onChange={handleChange}
                                                        onPaste={(e) => e.preventDefault()}
                                                        placeholder={t("Email confirmation") + "*"}
                                                        aria-label={t("Email confirmation")}
                                                        aria-required="true"
                                                        aria-invalid={errors.desconfEmail ? "true" : "false"}
                                                        aria-describedby={errors.desconfEmail ? "error-desconfEmail" : undefined}
                                                    />
                                                </div>
                                                <FieldError id="desconfEmail" error={errors.desconfEmail} />
                                            </div>

                                                                                                        {store.sendLaterEnabled && (
                                                <div className="formField">
                                                    <label className="block typoMedium secondColor alignCenter" id="sendDateLabel">
                                                        {t("Date d'envoi de la carte cadeau par email")} :
                                                    </label>
                                                    <div className="formControl dateTimeLine" aria-labelledby="sendDateLabel">
                                                        <div className="grid p16" role="radiogroup" aria-label={t("Mode d'envoi")}>
                                                            <div className="cell6 alignCenter">
                                                                <a
                                                                    href="javascript:void(0)"
                                                                    className={`dateMode typoMedium ${dateTimeMode === 'now' ? 'active' : ''}`}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        handleDateTimeMode('now');
                                                                    }}
                                                                    role="button"
                                                                    aria-pressed={dateTimeMode === 'now'}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            handleDateTimeMode('now');
                                                                        }
                                                                    }}
                                                                >
                                                                    {t("Maintenant")}
                                                                </a>
                                                            </div>
                                                            <div className="cell6 alignCenter">
                                                                <a
                                                                    href="javascript:void(0)"
                                                                    className={`dateMode typoMedium ${dateTimeMode === 'after' ? 'active' : ''}`}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        handleDateTimeMode('after');
                                                                    }}
                                                                    role="button"
                                                                    aria-pressed={dateTimeMode === 'after'}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            handleDateTimeMode('after');
                                                                        }
                                                                    }}
                                                                >
                                                                    {t("Plus tard")}
                                                                </a>
                                                            </div>
                                                        </div>
                                                        {dateTimeMode === 'after' && (
                                                            <div className="grid p16">
                                                                <div className="cell6 datepickerBox">
                                                                    <DateTimePicker
                                                                        value={formData.datepicker || ''}
                                                                        onChange={(newDate) => {
                                                                            const { date, datepicker } = formatDateTime.handleDateChange(newDate, store);
                                                                            setFormData(prev => {
                                                                                const newData = {
                                                                                    ...prev,
                                                                                    date,
                                                                                    datepicker,
                                                                                    sendDate: `${date} ${prev.time || ''}`,
                                                                                    sendDateTmp: Math.floor(new Date(`${date} ${prev.time || ''}`).getTime() / 1000)
                                                                                };
                                                                                return newData;
                                                                            });
                                                                        }}
                                                                        format={store.format_date || 'DD/MM/YYYY'}
                                                                        locale={store.default_language || 'fr'}
                                                                        isDate={true}
                                                                        ariaLabel={t("Sélectionner une date")}
                                                                    />
                                                                </div>
                                                                <div className="cell6 timepickerBox">
                                                                    <DateTimePicker
                                                                        value={formData.timepicker || ''}
                                                                        onChange={(newTime) => {
                                                                            const { time, timepicker } = formatDateTime.handleTimeChange(newTime, store);
                                                                            setFormData(prev => {
                                                                                const newData = {
                                                                                    ...prev,
                                                                                    time,
                                                                                    timepicker,
                                                                                    sendDate: `${prev.date || ''} ${time}`,
                                                                                    sendDateTmp: Math.floor(new Date(`${prev.date || ''} ${time}`).getTime() / 1000)
                                                                                };
                                                                                return newData;
                                                                            });
                                                                        }}
                                                                        format="HH:mm"
                                                                        locale={store.default_language || 'fr'}
                                                                        isDate={false}
                                                                        showHeader={false}
                                                                        ariaLabel={t("Sélectionner une heure")}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                        <FieldError id="dateTime" error={errors.dateTime} />
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}

                            <div className="formField">
                                <div className="bottom10 alignCenter" aria-live="polite">
                                    {t("Les champs marqués d'un astérisque (*) sont obligatoires")}
                                </div>
                            </div>

                            <div className="actionsRow labelMargin alignCenter">
                                <input
                                    type="submit"
                                    name="submit"
                                    id="submit"
                                    className="btn"
                                    value={t("Validate")}
                                />
                            </div>
                        </div>
                    </form>
                    <AccessibleLink
                        onClick={onClose}
                        className="closeInfoContent info"
                        ariaLabel={t("Fermer")}
                    ></AccessibleLink>
                </div>
            </div>
        </div>
    );
};

export default UpdateCardModal;